import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { VOICE_IDENTIFICATION_URL } from '@utils/api/endpoints.voice-profiles.ts';
import {
  VoiceIdentificationsResponse,
  isActiveIdentificationStatus
} from '@utils/types/organization/voice-profile.ts';
import { VOICE_PROFILE_QUERY_KEYS } from '@utils/api/organizations/speakers/voice-profiles/query-keys.ts';

/** How often to re-read an active recognition job. Inference has a five-minute
 *  deadline server-side and the worker polls its outbox every 30 seconds, so a
 *  faster poll buys nothing and only adds requests. */
const ACTIVE_POLL_MS = 10_000;

/** Job status and per-label suggestions for one document.
 *
 * Polls only while the job is queued or running and stops on every terminal
 * status (completed, skipped, failed, not_requested) — an editor left open for
 * an afternoon must not keep asking about a job that finished. */
export const useVoiceIdentifications = (
  sessionId: string | undefined,
  documentId: string | undefined,
  enabled = true
) =>
  useQuery<VoiceIdentificationsResponse>(
    VOICE_PROFILE_QUERY_KEYS.identifications(sessionId ?? '', documentId ?? ''),
    async () => {
      const { data } = await Axios.get<VoiceIdentificationsResponse>(
        VOICE_IDENTIFICATION_URL.identifications(
          sessionId as string,
          documentId as string
        )
      );
      return data;
    },
    {
      enabled: enabled && !!sessionId && !!documentId,
      retry: false,
      useErrorBoundary: false,
      refetchInterval: data =>
        isActiveIdentificationStatus(data?.status) ? ACTIVE_POLL_MS : false,
      // Don't keep polling a job in a tab nobody is looking at.
      refetchIntervalInBackground: false
    }
  );
